import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { useKeycloak } from '@react-keycloak/web';
import {
  ArrowLeftIcon,
  BuildingLibraryIcon,
  PlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import type {
  TrustAccount,
  ClientTrustLedger,
  TrustTransaction,
  TrustAccountType,
  TrustReconciliationStatus,
} from '../types/billing.types';
import { TRUST_TRANSACTION_TYPE_LABELS } from '../types/billing.types';

const API_BASE = '';

const ACCOUNT_TYPE_LABELS: Record<TrustAccountType, string> = {
  iolta_pooled: 'IOLTA Pooled',
  dedicated_client: 'Dedicated Client',
  escrow: 'Escrow',
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const getReconStatusBadge = (status: TrustReconciliationStatus) => {
  switch (status) {
    case 'current':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"><CheckCircleIcon className="h-3 w-3" /> Current</span>;
    case 'overdue':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300"><ExclamationTriangleIcon className="h-3 w-3" /> Overdue</span>;
    case 'never_reconciled':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300"><ClockIcon className="h-3 w-3" /> Never Reconciled</span>;
  }
};

type Tab = 'ledgers' | 'transactions' | 'reconciliation';

const TrustAccountDetailPage: React.FC = () => {
  const { accountId } = useParams();
  const navigate = useAnimatedNavigate();
  const { keycloak } = useKeycloak();
  const [activeTab, setActiveTab] = useState<Tab>('ledgers');

  const [account, setAccount] = useState<TrustAccount | null>(null);
  const [ledgers, setLedgers] = useState<ClientTrustLedger[]>([]);
  const [transactions, setTransactions] = useState<TrustTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create ledger form
  const [showLedgerForm, setShowLedgerForm] = useState(false);
  const [ledgerClientId, setLedgerClientId] = useState('');
  const [ledgerMatterId, setLedgerMatterId] = useState('');
  const [ledgerThreshold, setLedgerThreshold] = useState('');
  const [ledgerEvergreen, setLedgerEvergreen] = useState(false);
  const [ledgerSubmitting, setLedgerSubmitting] = useState(false);
  const [ledgerError, setLedgerError] = useState<string | null>(null);

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  const fetchAll = useCallback(async () => {
    if (!accountId || !keycloak?.token) return;
    setLoading(true);
    setError(null);
    try {
      const headers = getHeaders();
      const [accRes, ledgerRes, txnRes] = await Promise.all([
        fetch(`${API_BASE}/api/v1/billing/trust-accounts/${accountId}`, { headers }),
        fetch(`${API_BASE}/api/v1/billing/trust-accounts/ledgers/all?trust_account_id=${accountId}`, { headers }),
        fetch(`${API_BASE}/api/v1/billing/trust-transactions?trust_account_id=${accountId}`, { headers }),
      ]);

      if (!accRes.ok) throw new Error(`Failed to load account: ${accRes.statusText}`);
      const accJson = await accRes.json();
      setAccount(accJson.data);

      if (ledgerRes.ok) {
        const lJson = await ledgerRes.json();
        setLedgers(lJson.data || []);
      }
      if (txnRes.ok) {
        const tJson = await txnRes.json();
        setTransactions(tJson.data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trust account');
    } finally {
      setLoading(false);
    }
  }, [accountId, keycloak?.token, getHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateLedger = async () => {
    if (!ledgerClientId.trim()) { setLedgerError('Client ID is required.'); return; }
    setLedgerSubmitting(true);
    setLedgerError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/billing/trust-accounts/ledgers`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          trust_account_id: accountId,
          client_id: ledgerClientId,
          matter_id: ledgerMatterId || undefined,
          minimum_balance_threshold: ledgerThreshold ? Number(ledgerThreshold) : undefined,
          is_evergreen: ledgerEvergreen,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.description || json?.error || 'Failed to create ledger');
      setShowLedgerForm(false);
      setLedgerClientId(''); setLedgerMatterId(''); setLedgerThreshold(''); setLedgerEvergreen(false);
      await fetchAll();
    } catch (err) {
      setLedgerError(err instanceof Error ? err.message : 'Failed to create ledger');
    } finally {
      setLedgerSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin h-6 w-6 border-2 border-teal-500 border-t-transparent rounded-full" />
        <span className="ml-2 text-sm text-gray-500">Loading trust account...</span>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12 text-center">
        <p className="text-red-500 mb-4">{error || 'Account not found'}</p>
        <button onClick={() => navigate('/billing/trust-accounts')} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm">
          Back to Trust Accounts
        </button>
      </div>
    );
  }

  const tabClass = (tab: Tab) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
      activeTab === tab
        ? 'border-teal-600 text-teal-600 dark:text-teal-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
    }`;

  return (
    <div className="max-w-7xl mx-auto px-6 py-6 space-y-6 lex-tab-panel">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/billing/trust-accounts')} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <BuildingLibraryIcon className="h-6 w-6 text-teal-600 dark:text-teal-400" />
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{account.account_name}</h1>
            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {ACCOUNT_TYPE_LABELS[account.account_type]}
            </span>
            {!account.is_active && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">Inactive</span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {account.bank_name}
            {account.account_number_masked && ` \u2022 ${account.account_number_masked}`}
          </p>
        </div>
      </div>

      {/* Account Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border border-teal-200 dark:border-teal-800">
          <div className="text-teal-600 dark:text-teal-400 text-sm font-medium">Book Balance</div>
          <div className="text-2xl font-bold text-teal-900 dark:text-teal-100">{formatCurrency(Number(account.current_book_balance))}</div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="text-blue-600 dark:text-blue-400 text-sm font-medium">Client Ledgers</div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{ledgers.length}</div>
          <div className="text-xs text-blue-500 mt-1">{ledgers.filter(l => l.status === 'active').length} active</div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="text-purple-600 dark:text-purple-400 text-sm font-medium">Transactions</div>
          <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">{transactions.length}</div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">Reconciliation</div>
          <div className="mt-1">{getReconStatusBadge(account.reconciliation_status)}</div>
          {account.last_reconciled_date && (
            <div className="text-xs text-gray-500 mt-1">Last: {formatDate(account.last_reconciled_date)}</div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-0">
          <button className={tabClass('ledgers')} onClick={() => setActiveTab('ledgers')}>Client Ledgers</button>
          <button className={tabClass('transactions')} onClick={() => setActiveTab('transactions')}>Transaction Register</button>
          <button className={tabClass('reconciliation')} onClick={() => setActiveTab('reconciliation')}>Reconciliation</button>
        </div>
      </div>

      {/* Tab Content */}
      <div key={activeTab} className="lex-tab-panel">
      {activeTab === 'ledgers' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Client Ledgers</h3>
            <button onClick={() => setShowLedgerForm(!showLedgerForm)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
              <PlusIcon className="h-4 w-4" /> New Ledger
            </button>
          </div>

          {showLedgerForm && (
            <div className="bg-teal-50 dark:bg-teal-900/10 border border-teal-200 dark:border-teal-800 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Client ID *</label>
                  <input type="text" value={ledgerClientId} onChange={(e) => setLedgerClientId(e.target.value)} placeholder="Client UUID"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Matter ID (optional)</label>
                  <input type="text" value={ledgerMatterId} onChange={(e) => setLedgerMatterId(e.target.value)} placeholder="Matter UUID"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Minimum Balance Threshold</label>
                  <input type="number" step="0.01" min="0" value={ledgerThreshold} onChange={(e) => setLedgerThreshold(e.target.value)} placeholder="0.00"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 py-1.5 cursor-pointer">
                    <input type="checkbox" checked={ledgerEvergreen} onChange={(e) => setLedgerEvergreen(e.target.checked)}
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Evergreen retainer</span>
                  </label>
                </div>
              </div>
              {ledgerError && <p className="text-xs text-red-500">{ledgerError}</p>}
              <div className="flex justify-end gap-2">
                <button onClick={() => { setShowLedgerForm(false); setLedgerError(null); }}
                  className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                  Cancel
                </button>
                <button onClick={handleCreateLedger} disabled={ledgerSubmitting}
                  className="px-4 py-1.5 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
                  {ledgerSubmitting ? 'Creating...' : 'Create Ledger'}
                </button>
              </div>
            </div>
          )}

          {ledgers.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
              No client ledgers yet. Create one to start tracking client trust balances.
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Client ID</th>
                    <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Matter ID</th>
                    <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Balance</th>
                    <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Deposits</th>
                    <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Disbursements</th>
                    <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
                    <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Evergreen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {ledgers.map((ledger) => (
                    <tr key={ledger.client_trust_ledger_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-3 py-2 text-gray-900 dark:text-white font-mono text-xs">{ledger.client_id.slice(0, 8)}...</td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono text-xs">
                        {ledger.matter_id ? `${ledger.matter_id.slice(0, 8)}...` : '-'}
                      </td>
                      <td className={`px-3 py-2 text-right font-medium ${
                        Number(ledger.current_balance) > 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-900 dark:text-white'
                      }`}>
                        {formatCurrency(Number(ledger.current_balance))}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{formatCurrency(Number(ledger.total_deposits))}</td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{formatCurrency(Number(ledger.total_disbursements))}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                          ledger.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                            : ledger.status === 'closed'
                            ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                        }`}>
                          {ledger.status.charAt(0).toUpperCase() + ledger.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-gray-500">
                        {ledger.is_evergreen ? 'Yes' : 'No'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Transaction Register
            <span className="text-sm font-normal text-gray-400 ml-2">({transactions.length})</span>
          </h3>

          {transactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
              No transactions recorded yet.
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                    <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Type</th>
                    <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                    <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Source / Payee</th>
                    <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Debit</th>
                    <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Credit</th>
                    <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Balance</th>
                    <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {transactions.map((txn) => (
                    <tr key={txn.trust_transaction_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-3 py-2 text-gray-900 dark:text-white whitespace-nowrap">{formatDate(txn.transaction_date)}</td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap text-xs">
                        {TRUST_TRANSACTION_TYPE_LABELS[txn.transaction_type] || txn.transaction_type}
                      </td>
                      <td className="px-3 py-2 text-gray-900 dark:text-white max-w-xs truncate">{txn.description}</td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{txn.source_or_payee || '-'}</td>
                      <td className="px-3 py-2 text-right text-red-600 dark:text-red-400 whitespace-nowrap">
                        {txn.direction === 'debit' ? formatCurrency(Number(txn.amount)) : ''}
                      </td>
                      <td className="px-3 py-2 text-right text-green-600 dark:text-green-400 whitespace-nowrap">
                        {txn.direction === 'credit' ? formatCurrency(Number(txn.amount)) : ''}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-medium whitespace-nowrap">
                        {formatCurrency(Number(txn.balance_after))}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                          txn.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                          txn.status === 'reversed' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300' :
                          txn.status === 'void' ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' :
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                        }`}>
                          {txn.status.charAt(0).toUpperCase() + txn.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'reconciliation' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Reconciliation</h3>
            <button
              onClick={() => navigate(`/billing/trust-accounts/${accountId}/reconcile`)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Start Reconciliation
            </button>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</div>
                <div className="mt-1">{getReconStatusBadge(account.reconciliation_status)}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Last Reconciled</div>
                <div className="text-gray-900 dark:text-white mt-1">
                  {account.last_reconciled_date ? formatDate(account.last_reconciled_date) : 'Never'}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Book Balance</div>
                <div className="text-gray-900 dark:text-white mt-1 font-semibold">
                  {formatCurrency(Number(account.current_book_balance))}
                </div>
              </div>
            </div>

            <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Three-way reconciliation compares the bank statement balance, book balance, and sum of client ledger balances.
              All three must match for a balanced reconciliation.
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default TrustAccountDetailPage;
